import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import * as Constants from '@emi/shared/constants';
import { frequencyOptions, pricePoints } from '@emi/shared/constants';
import { ConfigureData, DateRange, DynamicExchangeRate, ExchangeRate, Frequency, SearchOption } from '@emi/shared/models';
import { DataExtractionState, addConfigureDataSelection, getExchangeRates, getSelectedConfigureData, getViaExchangeRates } from '@emi/shared/state/filters';
import { SkuMetadataState, fetchSkuMetadata, getSkuMetadataState } from '@emi/shared/state/sku-metadata';
import { SkuSearchState, SortOptionsByPageState, getSkuSearchResults, selectOrderByTLP, selectSkuCount, selectSkuSearchState, selectSortByTLP, setCurrency, setOrderByTimeline, setSortByTimeline } from '@emi/shared/state/sku-search';
import { Store, select } from '@ngrx/store';
import { DatePickerSettings } from 'libs/shared/ui/src/lib/date-range/models';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { ExchangeRateModal } from '../exchange-rate-modal/exchange-rate-modal.component';
import { take } from 'rxjs/operators';

@Component({
    selector: 'via-sku-time-series-filter-bar',
    templateUrl: './sku-time-series-filter-bar.component.html',
    styleUrls: ['./sku-time-series-filter-bar.component.scss'],
})
export class SkuTimeSeriesFilterBarComponent implements OnInit, OnDestroy {
    @Output()
    public showAllPricesClick = new EventEmitter<boolean>();

    @Output()
    public displayedPriceClick = new EventEmitter<boolean>();

    public calculatedExchangeRates: ExchangeRate[] = [];

    private configureDataSubscription: Subscription;

    public minDate: Date;
    public maxDate: Date;
    public maxSelectableDays = 0;
    public dateSettings: DatePickerSettings;
    public skuCount = 0;
    public selectedCurrency = 'USD';
    public skuCountMessage = '';
    public configureData = new ConfigureData();
    private skuMetadataSubscription: Subscription;
    private skuCountSubscription: Subscription;
    private skuSearchSubscription: Subscription;

    //TODO: Get copy for tooltips
    public viewAsTooltipText = 'View price tracking information for SKUs at an overall item level, or for SKUs split by different retailers (where available)';
    public exRateTooltipText = 'The conversion rates for different currency options available under your subscription';
    public selectedSortType = '';
    public selectedTableAggregation = Constants.Application.DEFAULT_VIEW_BY;
    public priceDisplayValues = { shelfPrice: 'shelfPrice', regularPrice: 'regularPrice' };
    public selectedPricePerOption = Constants.Application.DEFAULT_PRICE_PER;
    public selectedFrequencyOption = Constants.Application.DEFAULT_FREQUENCY;
    public selectedDateRange: DateRange = { startDate: undefined, endDate: undefined };
    public pricePointTooltipText = Constants.Tooltips.PRICE_POINT;

    public sortOrder = '';
    public priceDisplayed = this.priceDisplayValues.shelfPrice;
    public isShowingAllPrices = true;
    public showRegularPrice = false;

    public pricePerOptions = [
        { value: pricePoints[0], label: 'SKU' },
        { value: pricePoints[1], label: 'Unit' },
    ];

    public frequencyOptions = frequencyOptions;
    public searchTerm: string;
    public searchOption: SearchOption;
    public includeSearchPhrase: boolean | undefined;

    public tableAggregations = [
        { value: 'Retailers', label: 'SKUs By Retailers' },
        { value: 'AcrossRetailers', label: 'SKUs Across Retailers' },
    ];

    public sortByTypes = [
        { value: '', label: 'SKU Details' },
        { value: 'totalSkuSize', label: 'Total SKU Size' },
        { value: 'totalRetailers', label: 'Total Retailers' },

    ];

    public priceDisplays = [
        { label: 'Shelf Price', tooltip: 'Actual sale price (reflects promotional effects)', value: this.priceDisplayValues.shelfPrice },
        { label: 'Regular Price', tooltip: 'Most recent non-promotional price', value: this.priceDisplayValues.regularPrice },
    ];

    constructor(
        private router: Router,
        public dialog: MatDialog,
        public dataExtractionStore: Store<DataExtractionState>,
        private skuMetadataStore: Store<SkuMetadataState>,
        private skuSearchStore: Store<SkuSearchState>,
        private sortOptionsByPageStore: Store<SortOptionsByPageState>
    ) {
        this.skuMetadataStore.dispatch(fetchSkuMetadata());

        this.minDate = moment().utc().subtract(Constants.DatePicker.MAX_ALLOWED_YEARS_AGO, 'years').startOf('year').toDate();

        this.skuMetadataSubscription = this.skuMetadataStore.pipe(select(getSkuMetadataState)).subscribe((skuMetadata) => {
            if (skuMetadata.lastUpdated) {
                this.maxDate = skuMetadata.lastUpdated;
                return;
            }
            this.maxDate = moment().utc().subtract(Constants.DatePicker.DEFAULT_MAX_DATE_DAYS_AGO, 'days').toDate();
        });
    }

    public ngOnInit(): void {
        this.updateShowAllPrices();

        this.skuSearchSubscription = this.skuSearchStore.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.searchTerm = state.searchTerm;
            this.searchOption = state.searchOption;
            this.includeSearchPhrase = state.includeSearchPhrase;
        });

        this.configureDataSubscription = this.dataExtractionStore.pipe(select(getSelectedConfigureData)).subscribe((x) => {
            this.configureData = x;
            this.selectedPricePerOption = this.configureData.pricePoint;
            this.selectedDateRange.startDate = this.configureData.startDate;
            this.selectedDateRange.endDate = this.configureData.endDate;
            this.selectedTableAggregation = this.configureData.viewBy;
            this.selectedCurrency = x.currency;
            if (this.selectedDateRange.startDate == undefined || this.selectedDateRange.endDate == undefined) {
                this.selectDefaultDateRange();
            }
        });

        this.dateSettings = new DatePickerSettings(
            new DateRange(this.selectedDateRange.startDate, this.selectedDateRange.endDate),
            new DateRange(this.minDate, this.maxDate)
        );

        this.skuCountSubscription = this.skuSearchStore.pipe(select(selectSkuCount)).subscribe((skuCount) => (this.skuCount = skuCount));
        this.getExchangeRates();

        this.getSelectedSortOptionsFromStore();
    }

    public getSelectedSortOptionsFromStore(): void {
        this.sortOptionsByPageStore.pipe(select(selectSortByTLP), take(1)).subscribe((sortByTLP) => {
            this.selectedSortType = sortByTLP;
        });
        
        this.sortOptionsByPageStore.pipe(select(selectOrderByTLP), take(1)).subscribe((orderByTLP) => {
            this.sortOrder = orderByTLP;
        });
    }

    public updateDateRange(dateRange: DateRange) {
        if (dateRange.startDate && dateRange.endDate) {
            const configureData = { ...this.configureData, startDate: dateRange.startDate, endDate: dateRange.endDate };
            this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        }
    }

    public onSKUsViewChange(event: MatSelectChange): void {
        if (event.value) {
            const configureData = { ...this.configureData, viewBy: event.value };
            this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        }
    }

    public updateFrequency(frequency: Frequency): void {
        const configureData = { ...this.configureData, frequency: frequency };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.maxSelectableDays = this.getMaxSelectableDays();
    }

    public updatePricePer(event: MatSelectChange) {
        const configureData = { ...this.configureData, pricePoint: event.value };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
    }

    public isPriceDisplayChecked(priceDisplay: string) {
        return this.priceDisplayed === priceDisplay;
    }

    public updateShowAllPrices(): void {
        this.showAllPricesClick.emit(this.isShowingAllPrices);
    }


    public updatePriceDisplayed(displayedPrice: string) {

        this.showRegularPrice = displayedPrice !== 'shelfPrice';

        this.displayedPriceClick.emit(this.showRegularPrice);
    }

    public updateCurrency(currency: string) {
        this.selectedCurrency = currency;
        this.putSelectedCurrencyOnTop();

        const configureData = { ...this.configureData, currency: currency };

        this.skuSearchStore.dispatch(setCurrency({ currency: currency }));
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.dataExtractionStore.dispatch(getExchangeRates({ currency }));
    }

    public onSortByChange() {
        this.sortOptionsByPageStore.dispatch(setSortByTimeline({ sortBy: this.selectedSortType }));

        this.skuSearchStore.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: this.searchOption,
                sortBy: this.selectedSortType,
                orderBy: this.sortOrder,
                includeSearchPhrase: this.includeSearchPhrase
            })
        );
    }

    public toggleSortOrder(): void {
        this.sortOrder === 'descending' ? (this.sortOrder = 'ascending') : (this.sortOrder = 'descending');

        this.sortOptionsByPageStore.dispatch(setOrderByTimeline({ orderBy: this.sortOrder }));

        this.skuSearchStore.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: this.searchOption,
                sortBy: this.selectedSortType,
                orderBy: this.sortOrder,
                includeSearchPhrase: this.includeSearchPhrase
            })
        );
    }

    public openCurrencyConversionModal(): void {
        this.dialog.open(ExchangeRateModal, {
            data: { selectedCurrency: this.selectedCurrency, exchangeRateData: this.calculatedExchangeRates },
        });
    }

    public navigateToConfigureData(): void {
        this.router.navigate([Constants.Routes.SKU_SEARCH_CONFIGURE_DATA_ROUTE]);
    }

    private putSelectedCurrencyOnTop(): void {
        const selectedCurrencyIndex = this.calculatedExchangeRates.findIndex((rate) => rate.name === this.selectedCurrency);
        const topValue = this.calculatedExchangeRates.splice(selectedCurrencyIndex, 1)[0];
        this.calculatedExchangeRates.unshift(topValue);
    }

    private getExchangeRates() {
        this.dataExtractionStore.pipe(select(getViaExchangeRates)).subscribe((results) => {

            this.calculatedExchangeRates = results.map((exchangeRate: DynamicExchangeRate) => new ExchangeRate(exchangeRate.id, exchangeRate.toCurrencyName, exchangeRate.toCurrencyFullName,
                parseFloat(exchangeRate.exchangeRateValue).toFixed(3)));

            this.putSelectedCurrencyOnTop();
        });
    }

    private selectDefaultDateRange(): void {
        this.selectedDateRange = DateRange.getDefaultDateRange(this.maxDate);
        this.updateDateRange(this.selectedDateRange);
    }

    private getMaxSelectableDays(): number {
        const dateRangeLimit = Constants.DatePicker.DATE_RANGE_LIMITS.find((limit) => {
            return (
                limit.frequency === this.configureData.frequency &&
                this.skuCount >= limit.skuCountLower &&
                this.skuCount <= limit.skuCountUpper
            );
        });
        if (dateRangeLimit) {
            this.skuCountMessage = dateRangeLimit.message;
            return dateRangeLimit.maxSelectableDays;
        }

        this.skuCountMessage = '';
        return 0;
    }

    public ngOnDestroy(): void {
        this.configureDataSubscription.unsubscribe();
        this.skuMetadataSubscription.unsubscribe();
        this.skuCountSubscription.unsubscribe();
        this.skuSearchSubscription.unsubscribe();
    }
}
